import { useEffect, useState, useCallback } from 'react';
import {
    useGetMyWorksitesMutation,
    useGetWorksiteProgressMutation,
    useGetWorksiteInfoMutation,
    GetWorksiteProgressResponse,
    GetWorksiteInfoResponse
} from '@services';

export type WorksiteData = {
    id: number;
    progress: GetWorksiteProgressResponse;
    info: GetWorksiteInfoResponse;
};

export const useMyWorksiteResultsPage = () => {
    const [getMyWorksites, { data: worksitesData, isLoading: isLoadingWorksites }] = useGetMyWorksitesMutation();
    const [getWorksiteProgress] = useGetWorksiteProgressMutation();
    const [getWorksiteInfo] = useGetWorksiteInfoMutation();
    const [worksitesWithData, setWorksitesWithData] = useState<WorksiteData[]>([]);
    const [isLoadingProgress, setIsLoadingProgress] = useState(false);

    // Récupérer la liste des chantiers au chargement
    useEffect(() => {
        getMyWorksites();
    }, [getMyWorksites]);

    // Pour chaque chantier, récupérer les données de progression et les infos
    const fetchWorksitesData = useCallback(async () => {
        if (worksitesData && worksitesData.length > 0) {
            setIsLoadingProgress(true);
            const worksitePromises = worksitesData.map(async (worksite) => {
                try {
                    const [progressResponse, infoResponse] = await Promise.all([
                        getWorksiteProgress({ worksiteId: worksite.id }).unwrap(),
                        getWorksiteInfo(worksite.id.toString()).unwrap()
                    ]);

                    return {
                        id: worksite.id,
                        progress: progressResponse,
                        info: infoResponse
                    };
                } catch (error) {
                    console.error(`Erreur lors de la récupération des données du chantier ${worksite.id}:`, error);
                    return null;
                }
            });

            const results = await Promise.all(worksitePromises);
            const validResults = results.filter((result): result is WorksiteData => result !== null);
            setWorksitesWithData(validResults);
            setIsLoadingProgress(false);
        }
    }, [worksitesData, getWorksiteProgress, getWorksiteInfo]);

    useEffect(() => {
        fetchWorksitesData();
    }, [fetchWorksitesData]);

    // Déterminer si le bilan est positif ou négatif (positif si consommé < budget)
    const getGlobalStatus = (progress: GetWorksiteProgressResponse): 'positive' | 'negative' => {
        return progress.global.consummed < progress.global.total ? 'positive' : 'negative';
    };

    const isLoading = isLoadingWorksites || isLoadingProgress;

    return {
        worksitesWithData,
        isLoading,
        getGlobalStatus
    };
};
